import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  selectTriggerVariants,
} from '@speaking-coach/ui'
import {
  Listbox,
  ListboxMessage,
  ListboxOption,
  ListboxSearch,
  useListboxId,
} from '../components/ui/listbox'
import { activityApi } from './api'
import { historyIdentity } from './content-history-model'
import type { ContentKind, HistoryTargetOption } from './content-history-types'

export function useHistoryTarget(kind: ContentKind, id: string) {
  const params = { targetType: kind, id }
  return useQuery({
    queryKey: queryKeys.historyTargets(params),
    queryFn: () => activityApi.historyTargets(params),
    enabled: historyIdentity.safeParse(id).success,
  })
}
export function HistoryTargetPicker({
  kind,
  value,
  label,
  id,
  onChange,
}: {
  kind: ContentKind
  value: string
  label: string
  id: string
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [search, setSearch] = useState('')
  const [active, setActive] = useState('')
  const cache = useQueryClient()
  const listId = useListboxId()
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 250)
    return () => clearTimeout(timer)
  }, [input])
  const params = { targetType: kind, q: search }
  const result = useQuery({
    queryKey: queryKeys.historyTargets(params),
    queryFn: () => activityApi.historyTargets(params),
    enabled: open,
  })
  const selected = useHistoryTarget(kind, value)
  const rows = result.data?.items ?? []
  const first = rows[0]?.id ?? ''
  useEffect(() => setActive(first), [first])
  function choose(row: HistoryTargetOption) {
    cache.setQueryData(
      queryKeys.historyTargets({ targetType: kind, id: row.id }),
      { ...result.data, items: [row], hasMore: false },
    )
    onChange(row.id)
    setOpen(false)
    setInput('')
    setSearch('')
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        role="combobox"
        aria-label={label}
        className={selectTriggerVariants({ size: 'sm' })}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setOpen(true)
          }
        }}
      >
        <span className="truncate">
          {!value
            ? `Select ${label.toLowerCase()}`
            : (selected.data?.items[0]?.label ??
              (selected.isPending ? 'Loading…' : 'Content unavailable'))}
        </span>
        <Icon name="choose" className="ml-auto shrink-0" />
      </PopoverTrigger>
      <PopoverContent className="w-(--anchor-width) p-0">
        <ListboxSearch
          listId={listId}
          label={`Search ${label.toLowerCase()}`}
          placeholder="Search name or content key"
          value={input}
          onValueChange={setInput}
          activeId={active}
          optionIds={rows.map((row) => row.id)}
          onActiveIdChange={setActive}
          onSelect={(candidate) => {
            const row = rows.find((row) => row.id === candidate)
            if (row) choose(row)
          }}
          onDismiss={() => setOpen(false)}
        />
        <Listbox id={listId} label={label}>
          {result.isPending && <ListboxMessage>Searching…</ListboxMessage>}
          {result.isError && (
            <ListboxMessage>
              Could not load content.{' '}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void result.refetch()}
              >
                Try again
              </Button>
            </ListboxMessage>
          )}
          {result.isSuccess && rows.length === 0 && (
            <ListboxMessage>
              No matching content. Try another name or key.
            </ListboxMessage>
          )}
          {rows.map((row) => (
            <ListboxOption
              key={row.id}
              listId={listId}
              value={row.id}
              active={active === row.id}
              onHover={() => setActive(row.id)}
              onSelect={() => choose(row)}
            >
              {row.label}
            </ListboxOption>
          ))}
          {result.data?.hasMore && (
            <ListboxMessage>
              Showing the first 20 matches. Keep typing to narrow the search.
            </ListboxMessage>
          )}
        </Listbox>
      </PopoverContent>
    </Popover>
  )
}
